import {
    BuildingOfficeIcon,
    ChatBubbleLeftRightIcon,
    GlobeAltIcon,
    LinkIcon,
    MapPinIcon,
    PhoneIcon,
    PhotoIcon,
} from "@heroicons/react/24/outline";
import MainButton from "../sharedComponents/MainButton";
import { useDispatch } from "react-redux";
import TextareaField from "../sharedComponents/TextareaField";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import InputField from "../sharedComponents/InputField";
import { getAllCompaniesAdmin, updateCompany } from "../../slices/7-company/thunk";
import { handleFileUpload } from "../../utils/uploadFiles";
import avatar from "../../assets/images/Avatar.avif";

const EditCompanyModal = ({ isOpen, onClose, company }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();

    // States
    const [logo, setLogo] = useState("");
    const [coverImage, setCoverImage] = useState("");
    const [name_EN, setNameEN] = useState("");
    const [name_AR, setNameAR] = useState("");
    const [about_EN, setAboutEN] = useState("");
    const [about_AR, setAboutAR] = useState("");
    const [short_bio_EN, setShortBioEN] = useState("");
    const [short_bio_AR, setShortBioAR] = useState("");
    const [contactInfo, setContactInfo] = useState({
        email: "",
        phone: "",
        website: "",
        address: "",
        socialMedia: { facebook: "", linkedin: "" },
    });

    // ✅ Fill data when modal opens
    useEffect(() => {
        if (company) {
            setLogo(company.logo || "");
            setCoverImage(company.coverImage || "");
            setNameEN(company.name_EN || "");
            setNameAR(company.name_AR || "");
            setAboutEN(company.about_EN || "");
            setAboutAR(company.about_AR || "");
            setShortBioEN(company.short_bio_EN || "");
            setShortBioAR(company.short_bio_AR || "");
            setContactInfo({
                email: company.contactInfo?.email || "",
                phone: company.contactInfo?.phone || "",
                website: company.contactInfo?.website || "",
                address: company.contactInfo?.address || "",
                socialMedia: {
                    facebook: company.contactInfo?.socialMedia?.facebook || "",
                    linkedin: company.contactInfo?.socialMedia?.linkedin || "",
                },
            });
        }
    }, [company]);

    const handleSubmit = async () => {
        const payload = {
            name_EN,
            name_AR,
            about_EN,
            about_AR,
            short_bio_EN,
            short_bio_AR,
            contactInfo,
            logo,
            coverImage,
        };

        try {
            await dispatch(
                updateCompany({
                    id: company.id,
                    data: payload,
                })
            ).unwrap();
            onClose();  
            dispatch(
                dispatch(getAllCompaniesAdmin({ page: 1, limit: 10 }))
            ).unwrap();
        } catch (error) {
            console.error("Update failed", error);
        }
    };

    return (
        <div
            className={`fixed inset-0 z-[999] flex items-center justify-center transition-all duration-300 ${isOpen ? "visible" : "invisible"
                }`}
        >
            {/* Overlay */}
            <div
                onClick={onClose}
                className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${isOpen ? "opacity-100" : "opacity-0"
                    }`}
            />

            {/* Modal */}
            <div
                className={`relative z-10 w-full max-w-3xl mx-4 rounded-2xl dark:bg-bg-dark-secondary bg-bg-light-secondary shadow-glow transform transition-all duration-300 ${isOpen
                    ? "opacity-100 scale-100 translate-y-0"
                    : "opacity-0 scale-95 translate-y-6"
                    }`}
            >
                <div className="max-h-[85vh] overflow-y-auto no-scrollbar p-5">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-2xl text-black font-xsemibold dark:text-white">
                            {t("company.update")}
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-xl text-gray-400 duration-300 hover:text-black dark:hover:text-white"
                        >
                            ✕
                        </button>
                    </div>

                    {/* Cover Image */}
                    <div className="relative mb-6">
                        <div className="w-full h-40 overflow-hidden rounded-2xl bg-bg-light-tertiary dark:bg-bg-dark-tertiary">
                            <img
                                src={coverImage || avatar}
                                alt="cover"
                                className="object-cover w-full h-full"
                            />
                        </div>

                        <label className="absolute p-2 rounded-full cursor-pointer bottom-3 right-3 bg-primary-light dark:bg-primary">
                            <PhotoIcon className="w-4 h-4 text-white" />
                            <input
                                type="file"
                                accept="image/png, image/jpeg"
                                hidden
                                onChange={(e) =>
                                    handleFileUpload(
                                        dispatch,
                                        e.target.files[0],
                                        "images",
                                        (url) => setCoverImage(url)
                                    )
                                }
                            />
                        </label>
                    </div>

                    <div className="flex items-center gap-4 mb-4">
                        {/* Avatar */}
                        <div className="relative">
                            <img
                                src={logo || avatar}
                                alt="avatar"
                                className="object-cover w-16 h-16 rounded-full lg:h-20 lg:w-20"
                            />

                            {/* Camera icon */}
                            <label className="absolute bottom-0 right-0 p-2 rounded-full cursor-pointer bg-primary-light dark:bg-primary">
                                <PhotoIcon className="w-4 h-4 text-white" />
                                <input
                                    type="file"
                                    accept="image/png, image/jpeg"
                                    hidden
                                    onChange={(e) =>
                                        handleFileUpload(dispatch, e.target.files[0], "images", (url) =>
                                            setLogo(url)
                                        )
                                    }
                                />
                            </label>
                        </div>

                        {/* Info */}
                        <div className="space-y-1">
                            <label
                            // className="px-4 py-2 text-sm border cursor-pointer rounded-xl border-border-light dark:border-border-dark hover:bg-bg-light-tertiary dark:hover:bg-bg-dark-tertiary"
                            >
                                {t("company.logo")}
                            </label>

                            <p className="pt-2 text-xs text-text-muted-light dark:text-text-muted-dark">
                                JPG, PNG. Max 5MB
                            </p>
                        </div>

                    </div>
                    {/* Fields */}
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <InputField
                            type="text"
                            placeholder={t("company.nameEN")}
                            value={name_EN}
                            onChange={(e) => setNameEN(e.target.value)}
                            icon={BuildingOfficeIcon}
                        />

                        <InputField
                            type="text"
                            placeholder={t("company.nameAR")}
                            value={name_AR}
                            onChange={(e) => setNameAR(e.target.value)}
                            icon={BuildingOfficeIcon}
                        />

                        <TextareaField
                            placeholder={t("company.aboutEN")}
                            value={about_EN}
                            onChange={(e) => setAboutEN(e.target.value)}
                        />

                        <TextareaField
                            placeholder={t("company.aboutAR")}
                            value={about_AR}
                            onChange={(e) => setAboutAR(e.target.value)}
                        />

                        <InputField
                            type="text"
                            placeholder={t("company.shortBioEN")}
                            value={short_bio_EN}
                            onChange={(e) => setShortBioEN(e.target.value)}
                            icon={ChatBubbleLeftRightIcon}
                        />

                        <InputField
                            type="text"
                            placeholder={t("company.shortBioAR")}
                            value={short_bio_AR}
                            onChange={(e) => setShortBioAR(e.target.value)}
                            icon={ChatBubbleLeftRightIcon}
                        />
                    </div>

                    {/* Contact Info */}
                    <div className="grid grid-cols-1 gap-4 mt-4 md:grid-cols-2">
                        <InputField
                            type="email"
                            placeholder={t("company.email")}
                            value={contactInfo.email}
                            onChange={(e) =>
                                setContactInfo({ ...contactInfo, email: e.target.value })
                            }
                        />

                        <InputField
                            type="text"
                            placeholder={t("company.phone")}
                            value={contactInfo.phone}
                            onChange={(e) =>
                                setContactInfo({ ...contactInfo, phone: e.target.value })
                            }
                            icon={PhoneIcon}
                        />

                        <InputField
                            type="text"
                            placeholder={t("company.website")}
                            value={contactInfo.website}
                            onChange={(e) =>
                                setContactInfo({ ...contactInfo, website: e.target.value })
                            }
                            icon={GlobeAltIcon}
                        />

                        <InputField
                            type="text"
                            placeholder={t("company.address")}
                            value={contactInfo.address}
                            onChange={(e) =>
                                setContactInfo({ ...contactInfo, address: e.target.value })
                            }
                            icon={MapPinIcon}
                        />

                        <InputField
                            type="text"
                            placeholder={t("company.facebook")}
                            value={contactInfo.socialMedia.facebook}
                            onChange={(e) =>
                                setContactInfo({
                                    ...contactInfo,
                                    socialMedia: {
                                        ...contactInfo.socialMedia,
                                        facebook: e.target.value,
                                    },
                                })
                            }
                            icon={LinkIcon}
                        />

                        <InputField
                            type="text"
                            placeholder={t("company.linkedin")}
                            value={contactInfo.socialMedia.linkedin}
                            onChange={(e) =>
                                setContactInfo({
                                    ...contactInfo,
                                    socialMedia: {
                                        ...contactInfo.socialMedia,
                                        linkedin: e.target.value,
                                    },
                                })
                            }
                            icon={LinkIcon}
                        />
                    </div>

                    {/* Footer */}
                    <div className="flex justify-end mt-4">
                        <MainButton onClick={handleSubmit}>
                            {t("company.update")}
                        </MainButton>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditCompanyModal;
